import type { TypedFormFieldConfiguration } from '@risksmart-app/form-configuration/src/field-service';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type {
  FormEditorInitialData,
  FormEditorOutput,
} from 'src/blocks/form-editor-dialog';
import { useTRPC } from 'src/utils/trpc';

import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';

import type { FormConfigurationUpdatePayload } from './buildFormConfigurationUpdatePayload';
import { buildFormConfigurationUpdatePayload } from './buildFormConfigurationUpdatePayload';
import { buildStandardFieldCanonicalMap } from './conditionPropertyKeys';

interface UseFormEditorUpdateTRPCOptions {
  parentType: FormId;
  initialData: FormEditorInitialData;
  customAttributeSchema: FormConfigurationUpdatePayload['CustomAttributeSchema'];
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
}

/**
 * tRPC twin of the bulk save. Builds the normalised payload via the shared
 * helper, calls `frontend.formConfiguration.updateFormConfiguration`, and on
 * success invalidates the form-configuration query families (matching the
 * Apollo `evictFormCaches` set).
 */
export const useFormEditorUpdateTRPC = ({
  parentType,
  initialData,
  customAttributeSchema,
  formFieldConfigurations,
}: UseFormEditorUpdateTRPCOptions): ((
  output: FormEditorOutput
) => Promise<void>) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.formConfiguration.updateFormConfiguration.mutationOptions({
      onSuccess: async () => {
        // These two query families cover every current consumer of the form-
        // configuration tables: `getByParentTypes` backs EditFieldModal (lean,
        // fields_config only); `getFormCustomisation` backs
        // CustomisableFormDataProvider (full — all six form tables +
        // custom_attribute_schema). The Apollo twin evicts seven GraphQL
        // types one-by-one; here the two procedure-level invalidations are
        // equivalent because both procedures re-query the underlying tables
        // on next render. If a new tRPC reader is added against any of the
        // form_* tables or custom_attribute_schema, extend this list.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.formConfiguration.getByParentTypes.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.formConfiguration.getFormCustomisation.queryKey(),
          }),
        ]);
      },
    }),
  });

  const formConfig = useFormConfigRegistry()[parentType];
  const standardFieldCanonicalById = useMemo(
    () => buildStandardFieldCanonicalMap(formConfig),
    [formConfig]
  );

  return useCallback(
    async (output: FormEditorOutput) => {
      const payload = buildFormConfigurationUpdatePayload({
        output,
        parentType,
        initialData,
        customAttributeSchema,
        formFieldConfigurations,
        standardFieldCanonicalById,
      });

      // JsonSchema7 / VerticalLayout from @jsonforms/core are structurally
      // object records but lack the index signature the wire schema's
      // Record<string, unknown> requires, so route via `unknown`.
      const customAttributeSchemaForWire = payload.CustomAttributeSchema
        ? {
            Id: payload.CustomAttributeSchema.Id,
            Schema: payload.CustomAttributeSchema.Schema as unknown as Record<
              string,
              unknown
            >,
            UiSchema: payload.CustomAttributeSchema
              .UiSchema as unknown as Record<string, unknown>,
          }
        : undefined;

      await mutation.mutateAsync({
        ParentType: payload.ParentType,
        CustomAttributeSchema: customAttributeSchemaForWire,
        // Sent even when CustomAttributeSchema is null so a save with no
        // schema delta preserves the existing form_configuration → schema
        // link instead of severing it (the GraphQL twin does the same).
        CustomAttributeSchemaId: payload.CustomAttributeSchemaId,
        FieldConfigurations: payload.FieldConfigurations,
        FieldOrdering: payload.FieldOrdering,
        Sections: payload.Sections,
        CustomFieldsToDelete: payload.CustomFieldsToDelete,
        // Optional server-side: omitting it silently disables the guard.
        KnownSectionIds: payload.KnownSectionIds,
      });
    },
    [
      initialData,
      parentType,
      customAttributeSchema,
      formFieldConfigurations,
      mutation,
      standardFieldCanonicalById,
    ]
  );
};
